import { useState } from "react";

import { Modal } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import {
  useCreateInspectionSignatureMutation,
  type InspectionParticipant,
  type InspectionStatus,
} from "../../../api/inspection";
import {
  DEFAULT_INSPECTION_SIGNATURE_FONT_KEY,
  INSPECTION_SIGNATURE_FONTS,
  signatureFontFamily,
} from "../inspection-signature-fonts";

export interface InspectionSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  structureId: string;
  inspectionId: string;
  participant: InspectionParticipant;
  inspectionStatus?: InspectionStatus;
  onSigned?: () => void;
}

/**
 * Assinatura tipográfica (form §2.1.1) — nome da ficha somente leitura;
 * só a fonte é escolhível. Reassinar em `em andamento` reabre o mesmo fluxo.
 */
const InspectionSignatureModal = ({
  isOpen,
  onClose,
  structureId,
  inspectionId,
  participant,
  inspectionStatus = "inProgress",
  onSigned,
}: InspectionSignatureModalProps) => {
  const { translate } = useLocalization();
  const signedName = participant.name;
  const [fontKey, setFontKey] = useState(
    participant.signatureFontKey ?? DEFAULT_INSPECTION_SIGNATURE_FONT_KEY,
  );

  const { mutate: signParticipant } =
    useCreateInspectionSignatureMutation(structureId, inspectionId);

  const canSign = inspectionStatus === "inProgress";
  const nameInputId = `signature-name-${participant.id}`;

  const handleConfirm = () => {
    signParticipant(
      {
        participantId: participant.id,
        signedName,
        signatureFontKey: fontKey,
      },
      {
        onSuccess: () => {
          onSigned?.();
        },
      },
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={translate("inspection.signature.titleWithName", {
        name: participant.name,
      })}
      width="620px"
      content={
        <div className="insp-nested-shell insp-nested-shell--wide">
          <div className="insp-nested-body flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor={nameInputId}
              className="text-sm font-medium text-[var(--insp-heading,#333)]"
            >
              {translate("inspection.signature.name")}{" "}
              <span className="insp-req" aria-hidden>
                *
              </span>
            </label>
            <input
              id={nameInputId}
              value={signedName}
              readOnly
              aria-readonly="true"
              className="insp-inp"
            />
          </div>

          {canSign ? (
            <fieldset>
              <legend className="text-sm font-medium text-[var(--insp-heading,#333)]">
                {translate("inspection.signature.font")}
              </legend>
              <div
                role="radiogroup"
                aria-label={translate("inspection.signature.font")}
                className="insp-font-grid"
              >
                {INSPECTION_SIGNATURE_FONTS.map((font) => {
                  const selected = fontKey === font.key;
                  const label = translate(font.labelKey);

                  return (
                    <button
                      key={font.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={label}
                      className={`insp-font-opt${selected ? " is-sel" : ""}`}
                      onClick={() => setFontKey(font.key)}
                    >
                      <div
                        className="insp-font-smp"
                        style={{ fontFamily: font.fontFamily }}
                        aria-hidden
                      >
                        {signedName}
                      </div>
                      <div className="insp-font-nm">{label}</div>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          <div className="insp-sig-preview-box">
            <div className="insp-sig-cap">
              {translate("inspection.signature.preview")}
            </div>
            <p
              data-testid="signature-preview"
              data-font-key={fontKey}
              style={{ fontFamily: signatureFontFamily(fontKey) }}
              className="insp-sig-val"
            >
              {signedName}
            </p>
          </div>

          </div>

          <div className="insp-modal-foot">
            <button
              type="button"
              className="insp-btn insp-btn-outline"
              onClick={onClose}
            >
              {translate("inspection.actions.cancel")}
            </button>
            {canSign ? (
              <button
                type="button"
                className="insp-btn insp-btn-primary"
                onClick={handleConfirm}
              >
                {translate("inspection.signature.confirm")}
              </button>
            ) : null}
          </div>
        </div>
      }
    />
  );
};

export default InspectionSignatureModal;
